import React, { useState, useEffect, useRef } from "react";
import { db } from "../firebase";
import { ref, onValue } from "firebase/database";
import { useAuth } from "../context/AuthContext";
import { Bell, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

const TYPE_EMOJI = {
  offer: "💰",
  offer_accepted: "✅",
  offer_declined: "❌",
  price_drop: "📉",
  alert_match: "🔔",
  cross_college_request: "📩",
  request_approved: "✅",
  whatsapp_request: "📱",
  whatsapp_approved: "✅",
  admin_broadcast: "📢",
};

export default function InAppToast() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [toasts, setToasts] = useState([]);
  const seenIds = useRef(new Set());
  const startedAt = useRef(Date.now());

  useEffect(() => {
    if (!currentUser) return;
    const notifRef = ref(db, `notifications/${currentUser.uid}`);
    const unsub = onValue(notifRef, (snapshot) => {
      if (!snapshot.exists()) return;
      const all = snapshot.val();
      const newToasts = [];
      Object.entries(all).forEach(([id, notif]) => {
        if (seenIds.current.has(id)) return;
        seenIds.current.add(id);
        if (notif.read) return;
        // Only show notifications that arrived after the app opened (within 5s grace window)
        if ((notif.createdAt || 0) < startedAt.current - 5000) return;
        newToasts.push({
          id,
          message: notif.message || "New notification",
          type: notif.type,
          listingId: notif.listingId,
        });
      });
      if (newToasts.length === 0) return;
      setToasts(prev => [...prev, ...newToasts].slice(-3)); // max 3 at a time
      newToasts.forEach(t => {
        setTimeout(() => {
          setToasts(prev => prev.filter(x => x.id !== t.id));
        }, 5000);
      });
    });
    return () => unsub();
  }, [currentUser]);

  function dismiss(id) {
    setToasts(prev => prev.filter(x => x.id !== id));
  }

  function handleClick(toast) {
    dismiss(toast.id);
    if (toast.listingId) navigate(`/listing/${toast.listingId}`);
    else navigate("/notifications");
  }

  if (toasts.length === 0) return null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: "76px",
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 9998,
        width: "calc(100% - 32px)",
        maxWidth: "420px",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        pointerEvents: "none",
      }}
    >
      {toasts.map(toast => (
        <div
          key={toast.id}
          style={{
            background: "#1e293b",
            borderRadius: "14px",
            padding: "12px 14px",
            display: "flex",
            alignItems: "flex-start",
            gap: "10px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
            pointerEvents: "all",
            cursor: "pointer",
            animation: "slideUp 0.2s ease-out",
          }}
          onClick={() => handleClick(toast)}
        >
          <span style={{ fontSize: 18, flexShrink: 0, marginTop: "1px" }}>
            {TYPE_EMOJI[toast.type] || "🔔"}
          </span>
          <p style={{ flex: 1, fontSize: "13px", color: "#f1f5f9", lineHeight: 1.45, margin: 0 }}>
            {toast.message}
          </p>
          <button
            onClick={e => { e.stopPropagation(); dismiss(toast.id); }}
            style={{ flexShrink: 0, color: "#94a3b8", background: "none", border: "none", cursor: "pointer", padding: 0 }}
          >
            <X size={14} />
          </button>
        </div>
      ))}
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
